import type { Role } from '@glory/contracts';
import { authErrorMessage, formatDateTime, t } from '@glory/i18n';
import { MIN_PASSWORD_LENGTH } from '@glory/identity/domain';
import { AuthLayout, Badge, Button, Callout, PasswordField, SkeletonRows, TextField } from '@glory/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import { type SubmitEvent, useState } from 'react';

import { passwordProblem } from '../features/auth/password.ts';
import { authClient, authErrorCode } from '../lib/auth-client.ts';
import { orpc } from '../lib/orpc.ts';
import { resetSession } from '../lib/session.ts';
import { deviceName } from '../lib/user-agent.ts';

export const Route = createFileRoute('/invite/$token')({
  component: InvitePage,
});

type Stage = 'form' | 'passkey';

function InvitePage() {
  const { token } = Route.useParams();
  const preview = useQuery(
    orpc.identity.invitations.preview.queryOptions({ input: { token }, retry: false }),
  );
  const [stage, setStage] = useState<Stage>('form');
  // Админ выбрал форму работы — после входа сразу шаг «Данные и договор» (подпроект 4.4).
  const [next, setNext] = useState<'/' | '/profile/documents'>('/');

  // После принятия приглашение уже «использовано»: предпросмотр больше не нужен.
  if (stage === 'passkey') return <PasskeyOffer next={next} />;
  if (preview.isPending) {
    return (
      <AuthLayout title={t('auth.inviteTitle')}>
        <SkeletonRows rows={4} />
      </AuthLayout>
    );
  }
  if (preview.isError) {
    return (
      <AuthLayout title={t('auth.inviteTitle')}>
        <Callout>{preview.error.message}</Callout>
        <Button asChild variant="secondary" size="lg">
          <Link to="/login">{t('auth.toLogin')}</Link>
        </Button>
      </AuthLayout>
    );
  }
  return (
    <AcceptForm
      token={token}
      invitation={preview.data}
      onAccepted={(withContract) => {
        if (withContract) setNext('/profile/documents');
        setStage('passkey');
      }}
    />
  );
}

interface Invitation {
  readonly email: string;
  readonly name: string | null;
  readonly roles: readonly Role[];
  readonly position: string | null;
  readonly expiresAt: Date;
  readonly invitedByName: string | null;
}

function AcceptForm({
  token,
  invitation,
  onAccepted,
}: {
  token: string;
  invitation: Invitation;
  /** `withContract` — у приглашения есть форма работы: дальше шаг «Данные и договор». */
  onAccepted: (withContract: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(invitation.name ?? '');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<{ name?: string; password?: string; repeat?: string }>({});
  const [error, setError] = useState<string | null>(null);

  const accept = useMutation(
    orpc.identity.invitations.accept.mutationOptions({
      onSuccess: async ({ email, employmentForm }) => {
        const signedIn = await authClient.signIn.email({ email, password });
        if (signedIn.error) {
          setError(authErrorMessage(signedIn.error));
          return;
        }
        await resetSession(queryClient);
        onAccepted(employmentForm !== null);
      },
      onError: (failure) => {
        setError(failure.message);
      },
    }),
  );

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = {
      name: name.trim() === '' ? t('errors.identity.name_required') : undefined,
      password: passwordProblem(password),
      repeat: repeat !== password ? t('auth.passwordsMismatch') : undefined,
    };
    setErrors(next);
    if (next.name ?? next.password ?? next.repeat) return;
    setError(null);
    accept.mutate({ token, name, password });
  };

  return (
    <AuthLayout
      title={t('auth.inviteTitle')}
      lead={
        invitation.invitedByName
          ? t('auth.invitedBy', { name: invitation.invitedByName })
          : t('auth.invitedAnonymous')
      }
      footer={t('auth.inviteExpires', { date: formatDateTime(invitation.expiresAt) })}
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-lg border-thick border-border bg-surface px-3 py-2.5 text-small">
        <dt className="text-muted">{t('auth.email')}</dt>
        <dd className="truncate font-semibold">{invitation.email}</dd>
        <dt className="text-muted">{t('auth.inviteRoles')}</dt>
        <dd className="flex flex-wrap gap-1">
          {invitation.roles.map((role) => (
            <Badge key={role} tone="strong">
              {t(`roles.${role}`)}
            </Badge>
          ))}
        </dd>
        {invitation.position ? (
          <>
            <dt className="text-muted">{t('auth.invitePosition')}</dt>
            <dd className="font-semibold">{invitation.position}</dd>
          </>
        ) : null}
      </dl>
      <form className="flex flex-col gap-4" noValidate onSubmit={submit}>
        <TextField
          label={t('auth.yourName')}
          description={t('auth.yourNameHint')}
          autoComplete="name"
          autoFocus={invitation.name === null}
          required
          value={name}
          onValueChange={setName}
          error={errors.name}
          maxLength={200}
        />
        {/* Скрытое поле с адресом: менеджер паролей сохранит пару «email — пароль». */}
        <input type="email" autoComplete="username" value={invitation.email} readOnly hidden />
        <PasswordField
          label={t('auth.newPassword')}
          description={t('auth.newPasswordHint', { min: MIN_PASSWORD_LENGTH })}
          autoComplete="new-password"
          required
          value={password}
          onValueChange={setPassword}
          error={errors.password}
        />
        <PasswordField
          label={t('auth.repeatPassword')}
          autoComplete="new-password"
          required
          value={repeat}
          onValueChange={setRepeat}
          error={errors.repeat}
        />
        {error ? <Callout>{error}</Callout> : null}
        <Button type="submit" variant="primary" size="lg" loading={accept.isPending}>
          {t('auth.createAccount')}
        </Button>
      </form>
    </AuthLayout>
  );
}

function PasskeyOffer({ next }: { readonly next: '/' | '/profile/documents' }) {
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supported = typeof PublicKeyCredential !== 'undefined';
  const go = () => router.navigate({ to: next, replace: true });

  const add = async () => {
    setPending(true);
    setError(null);
    const { error: failure } = await authClient.passkey.addPasskey({ name: deviceName(navigator.userAgent) });
    setPending(false);
    if (failure) {
      if (authErrorCode(failure) !== 'REGISTRATION_CANCELLED') setError(authErrorMessage(failure));
      return;
    }
    await go();
  };

  return (
    <AuthLayout title={t('auth.passkeyOfferTitle')} lead={t('auth.passkeyOfferText')}>
      {error ? <Callout>{error}</Callout> : null}
      <div className="flex flex-col gap-2">
        {supported ? (
          <Button variant="primary" size="lg" loading={pending} onClick={() => void add()}>
            <KeyRound /> {t('auth.addPasskey')}
          </Button>
        ) : null}
        <Button variant={supported ? 'ghost' : 'primary'} size="lg" onClick={() => void go()}>
          {t('auth.later')}
        </Button>
      </div>
    </AuthLayout>
  );
}
